<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="pageTitle">
          <template v-slot:preview>
            <b-form>
              <!-- ===================== THÔNG TIN CHUNG ===================== -->
              <b-row class="mb-4">
                <b-col md="5">
                  <b-form-group>
                    <label>Tên chương trình <span class="text-danger">*</span></label>
                    <b-form-input
                      size="sm"
                      v-model="model.name"
                      placeholder="Nhập tên chương trình AdHoc"
                      autocomplete="off"
                    ></b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group>
                    <label>Từ ngày <span class="text-danger">*</span></label>
                    <date-picker
                      placeholder="Từ ngày"
                      class="form-control form-control-sm"
                      :config="dpConfigs.date"
                      v-model="model.startDate"
                    ></date-picker>
                  </b-form-group>
                </b-col>
                <b-col md="2">
                  <b-form-group>
                    <label>Đến ngày <span class="text-danger">*</span></label>
                    <date-picker
                      placeholder="Đến ngày"
                      class="form-control form-control-sm"
                      :config="dpConfigs.date"
                      v-model="model.endDate"
                    ></date-picker>
                  </b-form-group>
                </b-col>
                <b-col md="3">
                  <b-form-group>
                    <label>Trạng thái</label>
                    <b-form-select
                      size="sm"
                      v-model="model.status"
                      :options="statusOptions"
                      value-field="id"
                      text-field="name"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row class="mb-4">
                <b-col md="12">
                  <b-form-group>
                    <label>Mô tả</label>
                    <b-form-textarea
                      size="sm"
                      rows="2"
                      v-model="model.description"
                      placeholder="Mô tả chương trình"
                    ></b-form-textarea>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- ============ PHẠM VI: NHIỀU CH BÁN LẺ + BÁN BUÔN ============ -->
              <div class="section-title">Phạm vi áp dụng</div>
              <b-row class="mb-4">
                <b-col md="6">
                  <b-form-group>
                    <label>Cửa hàng bán lẻ</label>
                    <b-form-checkbox
                      class="mb-2"
                      v-model="model.applyAllRetail"
                      @change="onToggleApplyAllRetail"
                    >
                      Áp dụng toàn bộ cửa hàng bán lẻ
                    </b-form-checkbox>
                    <vue-multi-select
                      v-if="!model.applyAllRetail"
                      v-model="model.retailStores"
                      :options="stores"
                      placeholder="Chọn cửa hàng bán lẻ"
                      option-label="name"
                    ></vue-multi-select>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group>
                    <label>Cửa hàng bán buôn</label>
                    <b-form-checkbox
                      class="mb-2"
                      v-model="model.applyAllWholesale"
                      @change="onToggleApplyAllWholesale"
                    >
                      Áp dụng toàn bộ cửa hàng bán buôn
                    </b-form-checkbox>
                    <vue-multi-select
                      v-if="!model.applyAllWholesale"
                      v-model="model.wholesaleStores"
                      :options="stores"
                      placeholder="Chọn cửa hàng bán buôn"
                      option-label="name"
                    ></vue-multi-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- ==================== CHI TIẾT THƯỞNG ==================== -->
              <div class="section-title d-flex justify-content-between align-items-center">
                <span>Chi tiết thưởng</span>
                <div>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mr-2"
                    :disabled="!model.details.length"
                    @click="openBulkRuleModal"
                  >
                    <i class="fas fa-sliders-h mr-1"></i>Áp công thức cho tất cả
                  </b-button>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="mr-2"
                    @click="showImportModal"
                  >
                    <i class="far fa-file-excel mr-1"></i>Nhập Excel
                  </b-button>
                  <b-button variant="primary" size="sm" @click="openDetailModal">
                    <i class="flaticon2-add-1 mr-1"></i>Thêm
                  </b-button>
                </div>
              </div>

              <b-table
                v-if="model.details.length"
                class="table-bordered table-hover mb-3"
                :items="model.details"
                :fields="detailFields"
                small
                hover
                tbody-tr-class="detail-row"
                @row-clicked="showViewDetail"
              >
                <template v-slot:cell(count)="row">
                  {{ row.index + 1 }}
                </template>
                <template v-slot:cell(item)="row">
                  <b-badge
                    :variant="
                      row.item.type === ITEM_TYPE.PRODUCT ? 'primary' : 'secondary'
                    "
                    class="mr-1"
                    >{{ itemTypeName(row.item.type) }}</b-badge
                  >
                  {{ row.item.name }}
                </template>
                <template v-slot:cell(condition)="row">
                  <div>{{ describeAmount(row.item) }}</div>
                  <div class="text-muted small mt-1">
                    <b-badge
                      :variant="
                        row.item.rewardMode === REWARD_MODE.PLUS
                          ? 'info'
                          : 'secondary'
                      "
                      >{{ rewardModeName(row.item.rewardMode) }}</b-badge
                    >
                  </div>
                  <div
                    v-if="row.item.conditionCategories.length"
                    class="text-muted small mt-1"
                  >
                    Kèm:
                    {{
                      row.item.conditionCategories
                        .map((c) => c.name)
                        .join(
                          row.item.conditionLogic === CONDITION_LOGIC.AND
                            ? ' VÀ '
                            : ' HOẶC ',
                        )
                    }}
                  </div>
                </template>
                <template v-slot:cell(rates)="row">
                  <span
                    v-for="rate in describeRates(row.item)"
                    :key="rate.jobTitleId"
                    class="badge badge-light mr-1"
                    >{{ rate.name }}: {{ rate.amount }}</span
                  >
                </template>
                <template v-slot:cell(actions)="row">
                  <b-button
                    variant="link"
                    size="sm"
                    class="text-danger p-0"
                    @click.stop="removeDetail(row.index)"
                  >
                    <i class="flaticon2-rubbish-bin-delete-button"></i>
                  </b-button>
                </template>
              </b-table>

              <b-alert v-if="!model.details.length" show variant="secondary">
                Chưa có dòng chi tiết nào. Bấm <strong>Thêm</strong> hoặc
                <strong>Nhập Excel</strong> để bắt đầu.
              </b-alert>

              <!-- ======================== HÀNH ĐỘNG ======================== -->
              <div class="mt-4">
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-2"
                  :disabled="isSaving"
                  @click="save"
                >
                  <i class="flaticon2-check-mark mr-1"></i>
                  {{ isSaving ? 'Đang lưu...' : 'Lưu' }}
                </b-button>
                <b-button variant="secondary" size="sm" @click="goBack">
                  Huỷ
                </b-button>
              </div>
            </b-form>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- ============== MODAL THÊM DÒNG CHI TIẾT ==============
         Chỉ THÊM, không sửa: dòng đã thêm hiển thị ở bảng chỉ đọc.
         Sai thì xoá dòng rồi thêm lại — tránh sửa nhầm cấu hình đã chốt.

         Bố cục 3 khối theo đúng thứ tự người dùng nghĩ:
           1. Thưởng cho cái gì   2. Với điều kiện nào   3. Ai được bao nhiêu -->
    <b-modal
      v-model="detailModalVisible"
      title="Thêm dòng chi tiết thưởng"
      size="lg"
      @ok="confirmAddDetail"
      @hidden="resetDetailForm"
      ok-title="Thêm"
      cancel-title="Huỷ"
      :ok-disabled="!canAddDetail"
    >
      <!-- ---------- 1. Sản phẩm được thưởng ---------- -->
      <div class="form-block">
        <div class="form-block__title">1. Sản phẩm được thưởng</div>
        <b-row>
          <b-col md="4">
            <label class="form-label">Loại</label>
            <b-form-select
              size="sm"
              v-model="detailForm.type"
              :options="itemTypeOptions"
              value-field="id"
              text-field="name"
              @change="onDetailTypeChange"
            ></b-form-select>
          </b-col>
          <b-col md="8">
            <label class="form-label">
              {{ detailForm.type === ITEM_TYPE.PRODUCT ? 'Sản phẩm' : 'Danh mục' }}
              <span class="text-danger">*</span>
            </label>
            <Autosuggest
              :key="'modal-item-' + componentKey"
              :model="detailForm.name"
              :suggestions="itemSuggestions"
              :placeholder="
                detailForm.type === ITEM_TYPE.PRODUCT
                  ? 'Nhập tên hoặc mã sản phẩm'
                  : 'Nhập tên danh mục'
              "
              :limit="10"
              @select="onSelectItem($event)"
              @change="onSearchItem($event)"
              suggestionName="suggestionName"
            />
          </b-col>
        </b-row>
      </div>

      <!-- ---------- 2 + 3. Điều kiện và cách tính ---------- -->
      <AdhocRuleForm
        v-model="detailForm"
        :adhoc-categories="adhocCategories"
        :describe-revenue="describeRevenue"
        condition-title="2. Điều kiện áp dụng"
        reward-title="3. Khi sản phẩm đã có hoa hồng thường"
      />

      <!-- ---------- 4. Mức thưởng ---------- -->
      <div class="form-block form-block--last">
        <div class="form-block__title">
          4. Mức thưởng theo vị trí <span class="text-danger">*</span>
        </div>
        <b-alert v-if="!jobTitles.length" show variant="secondary" class="mb-0 py-2">
          Chưa tải được danh sách vị trí công việc.
        </b-alert>
        <div v-else class="rate-grid">
          <div v-for="job in jobTitles" :key="'mjob-' + job.id" class="rate-cell">
            <label class="rate-cell__label" :for="'rate-' + job.id">
              {{ job.name }}
            </label>
            <b-input-group size="sm" append="đ">
              <b-form-input
                :id="'rate-' + job.id"
                v-model="detailForm.rates[job.id]"
                placeholder="0"
                autocomplete="off"
                class="text-right"
              ></b-form-input>
            </b-input-group>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- ============== MODAL XEM DÒNG CHI TIẾT (CHỈ ĐỌC) ============== -->
    <b-modal
      v-model="viewDetailVisible"
      title="Chi tiết dòng thưởng"
      size="lg"
      ok-only
      ok-title="Đóng"
      ok-variant="secondary"
    >
      <template v-if="viewDetail">
        <div class="form-block">
          <div class="form-block__title">1. Sản phẩm được thưởng</div>
          <b-badge
            :variant="
              viewDetail.type === ITEM_TYPE.PRODUCT ? 'primary' : 'secondary'
            "
            class="mr-1"
            >{{ itemTypeName(viewDetail.type) }}</b-badge
          >
          <span class="font-weight-bold">{{ viewDetail.name }}</span>
          <span v-if="viewDetail.code" class="text-muted">
            (Mã: {{ viewDetail.code }})</span
          >
        </div>

        <div class="form-block">
          <div class="form-block__title">2. Điều kiện áp dụng</div>
          <label class="form-label">Doanh thu của dòng hàng</label>
          <div
            class="range-preview mb-3"
            :class="{ 'range-preview--warn': describeRevenue(viewDetail).warn }"
          >
            {{ describeRevenue(viewDetail).text }}
          </div>
          <label class="form-label">Nhóm AdHoc kèm theo</label>
          <div v-if="viewDetail.conditionCategories.length">
            <b-badge
              v-for="category in viewDetail.conditionCategories"
              :key="'vcat-' + category.id"
              variant="light"
              class="mr-1 mb-1"
              >{{ category.name }}</b-badge
            >
            <div
              v-if="viewDetail.conditionCategories.length > 1"
              class="text-muted small mt-1"
            >
              Cách ghép nhóm: {{ conditionLogicName(viewDetail.conditionLogic) }}
            </div>
          </div>
          <div v-else class="text-muted">Không yêu cầu nhóm kèm theo</div>
        </div>

        <div class="form-block">
          <div class="form-block__title">3. Khi sản phẩm đã có hoa hồng thường</div>
          <div>
            Cách tính:
            <strong>{{ rewardModeName(viewDetail.rewardMode) }}</strong>
          </div>
        </div>

        <div class="form-block form-block--last">
          <div class="form-block__title">4. Mức thưởng theo vị trí</div>
          <b-table
            class="table-bordered mb-0"
            :items="viewDetailRates"
            :fields="viewRateFields"
            small
            show-empty
            empty-text="Chưa có mức thưởng nào"
          ></b-table>
        </div>
      </template>
    </b-modal>

    <!-- ============== MODAL ÁP CÔNG THỨC CHO TẤT CẢ DÒNG ============== -->
    <b-modal
      v-model="bulkRuleVisible"
      title="Áp công thức cho tất cả dòng chi tiết"
      size="lg"
      :ok-title="'Áp dụng cho ' + model.details.length + ' dòng'"
      cancel-title="Huỷ"
      @ok="applyBulkRule"
    >
      <AdhocRuleForm
        v-model="bulkRule"
        :adhoc-categories="adhocCategories"
        :describe-revenue="describeRevenue"
        last
      />
    </b-modal>

    <!-- ============== MODAL NHẬP CHI TIẾT TỪ EXCEL ==============
         Chọn file -> tự kiểm tra (API) -> có lỗi thì chặn; sạch lỗi mới hiện
         phần điều kiện + cách tính áp chung cho mọi dòng, rồi Import vào bảng
         chi tiết (chưa lưu chương trình). -->
    <b-modal
      ref="import-modal"
      hide-footer
      size="xl"
      title="Nhập chi tiết thưởng AdHoc từ Excel"
    >
      <b-row>
        <b-col>
          <div>
            Chọn file Excel theo
            <b-link @click="downloadTemplate">
              <i class="fas fa-download mr-1" style="font-size: 0.9rem"></i>file
              mẫu</b-link
            >: cột <b>Loại</b>, <b>Mã</b>, <b>Tên</b> và mỗi vị trí công việc
            một cột tiền thưởng. Tối đa {{ IMPORT_MAX_ROWS }} dòng.
          </div>
          <div style="padding-top: 15px">
            <b-form-file
              placeholder="Hãy chọn một tập tin"
              @change="selectFile"
              v-model="file"
              ref="file-input"
              class="mb-2"
            ></b-form-file>
          </div>
        </b-col>
      </b-row>

      <div v-if="isValidating" class="text-muted mt-3">
        <b-spinner small class="mr-2"></b-spinner>Đang kiểm tra dữ liệu...
      </div>

      <!-- Danh sách lỗi: có lỗi thì KHÔNG cho lưu -->
      <b-container class="mt-4 pl-0 pr-0" v-if="importErrors.length > 0">
        <b-alert show variant="danger" class="mb-2">
          File có {{ importErrors.length }} lỗi. Vui lòng sửa file rồi tải lên
          lại — hệ thống không nhận dòng nào khi còn lỗi.
        </b-alert>
        <b-table
          class="table-bordered table-hover"
          :fields="errorFields"
          :items="importErrors"
          :per-page="IMPORT_PER_PAGE"
          :current-page="currentErrorPage"
        ></b-table>
        <b-pagination
          v-show="importErrors.length > IMPORT_PER_PAGE"
          v-model="currentErrorPage"
          :total-rows="importErrors.length"
          :per-page="IMPORT_PER_PAGE"
          align="right"
        ></b-pagination>
      </b-container>

      <!-- Xem trước dữ liệu hợp lệ -->
      <b-container
        class="mt-4 pl-0 pr-0"
        v-if="validatedItems.length > 0 && !importErrors.length"
      >
        <b-alert show variant="success" class="mb-2">
          Dữ liệu hợp lệ: {{ validatedItems.length }} dòng.
        </b-alert>
        <b-table
          class="table-bordered table-hover"
          :fields="importPreviewFields"
          :items="validatedItems"
          :per-page="IMPORT_PER_PAGE"
          :current-page="currentPreviewPage"
          small
        >
          <template v-slot:cell(type)="row">
            {{ itemTypeName(row.item.type) }}
          </template>
          <template v-slot:cell(employeeRates)="row">
            <span
              v-for="rate in row.item.employeeRates || []"
              :key="'irate-' + rate.jobTitleId"
              class="badge badge-light mr-1"
              >{{ rate.jobTitleName }}: {{ formatMoney(rate.amount) }}</span
            >
          </template>
        </b-table>
        <b-pagination
          v-show="validatedItems.length > IMPORT_PER_PAGE"
          v-model="currentPreviewPage"
          :total-rows="validatedItems.length"
          :per-page="IMPORT_PER_PAGE"
          align="right"
        ></b-pagination>

        <!-- File sạch lỗi mới hỏi công thức — áp chung cho mọi dòng vừa đọc -->
        <div class="mt-4">
          <AdhocRuleForm
            v-model="importRule"
            :adhoc-categories="adhocCategories"
            :describe-revenue="describeRevenue"
            condition-title="2. Điều kiện áp dụng"
            reward-title="3. Khi sản phẩm đã có hoa hồng thường"
            last
          />
        </div>
      </b-container>

      <div class="mt-4">
        <b-button
          style="width: 70px"
          variant="primary"
          size="sm"
          v-if="canApplyImport"
          @click="applyImport"
          >Import</b-button
        >
        <b-button
          style="margin-left: 10px; width: 70px"
          variant="secondary"
          size="sm"
          @click="hideImportModal"
          >Hủy</b-button
        >
      </div>
    </b-modal>
  </div>
</template>

<script>
/**
 * Tạo / cập nhật chương trình thưởng AdHoc V2 — SRS F-06.
 *
 * Hai điểm chính của màn này:
 *  1. Phạm vi áp dụng chọn được NHIỀU cửa hàng BÁN LẺ và NHIỀU cửa hàng BÁN BUÔN
 *     (hoặc bật cờ áp dụng toàn bộ từng kênh).
 *  2. Mỗi dòng chi tiết cấu hình được ĐIỀU KIỆN: ngưỡng doanh thu + các nhóm AdHoc
 *     kèm theo, ghép theo HOẶC / VÀ.
 *
 * API nhận cửa hàng dưới dạng phẳng [{storeId, scope}] nên hai danh sách bán lẻ /
 * bán buôn được gộp lại lúc build payload.
 *
 * Dòng chi tiết: bấm vào dòng để xem đầy đủ (chỉ đọc); xoá dòng phải xác nhận.
 * Nhập Excel: file đọc ở client, API validate-import-details đối chiếu mã/tên,
 * nhóm AdHoc, vị trí rồi mới đổ vào bảng — vẫn phải bấm Lưu chương trình.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import VueMultiSelect from 'vue-simple-multi-select';
import datePicker from 'vue-bootstrap-datetimepicker';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import Swal from 'sweetalert2';
import debounce from 'debounce';
import moment from 'moment';
import xlsx from 'xlsx';
import { TIME_TRIGGER } from '@/utils/constants';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import AdhocRuleForm, { emptyAdhocRule } from './AdhocRuleForm.vue';
import {
  COMMISSION_ADHOC_STATUS,
  COMMISSION_ADHOC_STATUS_NAME,
  COMMISSION_ADHOC_ITEM_TYPE,
  COMMISSION_ADHOC_ITEM_TYPE_NAME,
  COMMISSION_ADHOC_CONDITION_LOGIC,
  COMMISSION_ADHOC_CONDITION_LOGIC_NAME,
  COMMISSION_ADHOC_REWARD_MODE,
  COMMISSION_ADHOC_REWARD_MODE_NAME,
  COMMISSION_STORE_SCOPE,
  COMMISSION_SEARCH_TYPE,
  COMMISSION_IMPORT_MAX_ROWS,
  toOptionList,
} from './commission.constants';

const DATE_INPUT_FORMAT = 'DD/MM/YYYY';
const DATE_API_FORMAT = 'YYYY-MM-DD';
const IMPORT_PER_PAGE = 10;

/** Tiêu đề cột cố định của file Excel nhập chi tiết — sau đó là 1 cột / vị trí */
const IMPORT_COLUMN = Object.freeze({
  TYPE: 'Loại',
  CODE: 'Mã',
  NAME: 'Tên',
});

const IMPORT_ITEM_TYPE_MAP = Object.freeze({
  'sản phẩm': COMMISSION_ADHOC_ITEM_TYPE.PRODUCT,
  'danh mục': COMMISSION_ADHOC_ITEM_TYPE.CATEGORY,
});

export default {
  components: {
    KTCodePreview,
    Autosuggest,
    VueMultiSelect,
    datePicker,
    AdhocRuleForm,
  },
  data() {
    return {
      ITEM_TYPE: COMMISSION_ADHOC_ITEM_TYPE,
      CONDITION_LOGIC: COMMISSION_ADHOC_CONDITION_LOGIC,
      REWARD_MODE: COMMISSION_ADHOC_REWARD_MODE,
      componentKey: 0,
      isSaving: false,
      adhocId: null,
      model: {
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        status: COMMISSION_ADHOC_STATUS.ACTIVE,
        applyAllRetail: false,
        applyAllWholesale: false,
        retailStores: [],
        wholesaleStores: [],
        details: [],
      },
      stores: [],
      adhocCategories: [],
      jobTitles: [],
      itemSuggestions: [],
      detailModalVisible: false,
      detailForm: this.emptyDetailForm(),
      viewDetailVisible: false,
      viewDetail: null,
      viewRateFields: [
        { key: 'name', label: 'Vị trí' },
        { key: 'amount', label: 'Mức thưởng (đ)', tdClass: 'text-right' },
      ],
      IMPORT_MAX_ROWS: COMMISSION_IMPORT_MAX_ROWS,
      IMPORT_PER_PAGE,
      file: null,
      // Dòng đọc từ file (chỉ sản phẩm + mức thưởng); công thức lấy từ importRule lúc kiểm tra
      rawImportRows: [],
      importRule: emptyAdhocRule(),
      bulkRuleVisible: false,
      bulkRule: emptyAdhocRule(),
      validatedItems: [],
      importErrors: [],
      isValidating: false,
      currentErrorPage: 1,
      currentPreviewPage: 1,
      errorFields: [
        { key: 'row', label: 'Dòng', thStyle: { width: '10%' } },
        { key: 'message', label: 'Lỗi' },
      ],
      importPreviewFields: [
        { key: 'row', label: 'Dòng', tdClass: 'text-center' },
        { key: 'type', label: 'Loại' },
        { key: 'code', label: 'Mã' },
        { key: 'name', label: 'Tên' },
        { key: 'employeeRates', label: 'Mức thưởng' },
      ],
      detailFields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
        {
          key: 'item',
          label: 'Sản phẩm / Danh mục',
          thStyle: { fontWeight: 600, color: '#181c32', width: '30%' },
        },
        {
          key: 'condition',
          label: 'Điều kiện',
          thStyle: { fontWeight: 600, color: '#181c32', width: '30%' },
        },
        {
          key: 'rates',
          label: 'Mức thưởng',
          thStyle: { fontWeight: 600, color: '#181c32', width: '30%' },
        },
        {
          key: 'actions',
          label: '',
          thStyle: { fontWeight: 600, color: '#181c32', width: '5%' },
          tdClass: 'text-center',
        },
      ],
      statusOptions: toOptionList(COMMISSION_ADHOC_STATUS_NAME),
      itemTypeOptions: toOptionList(COMMISSION_ADHOC_ITEM_TYPE_NAME),
      dpConfigs: {
        date: {
          format: DATE_INPUT_FORMAT,
          useCurrent: false,
          showClear: true,
          showClose: true,
          locale: 'vi',
        },
      },
    };
  },
  computed: {
    /** Nút Thêm chỉ bật khi đã chọn item và có ít nhất một mức thưởng > 0 */
    canAddDetail() {
      if (!this.detailForm.referenceId) {
        return false;
      }
      return Object.keys(this.detailForm.rates).some(
        (jobTitleId) => this.toAmount(this.detailForm.rates[jobTitleId]) > 0,
      );
    },
    /** Mức thưởng > 0 của dòng đang xem, kèm tên vị trí */
    viewDetailRates() {
      if (!this.viewDetail) {
        return [];
      }
      const rates = this.viewDetail.rates || {};
      return Object.keys(rates)
        .filter((jobTitleId) => this.toAmount(rates[jobTitleId]) > 0)
        .map((jobTitleId) => {
          const job = this.jobTitles.find(
            (row) => Number(row.id) === Number(jobTitleId),
          );
          return {
            jobTitleId,
            name: job ? job.name : `#${jobTitleId}`,
            amount: this.formatMoney(rates[jobTitleId]),
          };
        });
    },
    canApplyImport() {
      return this.validatedItems.length > 0 && this.importErrors.length === 0;
    },
    pageTitle() {
      return this.adhocId
        ? 'Cập nhật chương trình thưởng AdHoc'
        : 'Tạo chương trình thưởng AdHoc';
    },
  },
  created() {
    this.adhocId = Number(this.$route.query.id) || null;
    this.fetchStores();
    this.fetchAdhocCategories();
    this.fetchJobTitles();
    if (this.adhocId) {
      this.fetchDetail();
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng', route: '/commission/categories' },
      { title: 'Thưởng AdHoc', route: '/commission/adhocs' },
      { title: this.adhocId ? 'Cập nhật' : 'Tạo mới' },
    ]);
  },
  methods: {
    /* ----------------------------- Nạp danh mục ---------------------------- */
    fetchStores() {
      ApiService.setHeader();
      ApiService.query('stores', { params: { limit: 1000, page: 1 } })
        .then(({ data }) => {
          this.stores = (data.data.list_store || []).map((store) => ({
            id: store.id,
            name: `${store.shortName} (${store.name})`,
          }));
        })
        .catch(() => makeToastFaile('Không tải được danh sách cửa hàng!'));
    },
    fetchAdhocCategories() {
      ApiService.setHeader();
      ApiService.query('commission-adhoc-categories', {
        params: { limit: 200, page: 1 },
      })
        .then(({ data }) => {
          this.adhocCategories = (data.data.items || []).map((row) => ({
            id: row.id,
            name: row.name,
          }));
        })
        .catch(() => makeToastFaile('Không tải được nhóm AdHoc!'));
    },
    fetchJobTitles() {
      ApiService.setHeader();
      ApiService.query('commission-job-titles/allowed', {
        params: { commissionType: 1 },
      })
        .then(({ data }) => {
          this.jobTitles = (data.data || []).map((row) => ({
            id: row.jobTitleId || row.id,
            name: row.jobTitleName || row.name,
          }));
        })
        .catch(() => makeToastFaile('Không tải được danh sách vị trí!'));
    },
    fetchDetail() {
      ApiService.setHeader();
      ApiService.query(`commission-adhocs/${this.adhocId}`)
        .then(({ data }) => this.bindModel(data.data))
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được chương trình'),
          );
        });
    },

    /* --------------------------- Đổ dữ liệu vào form ------------------------ */
    bindModel(adhoc) {
      const retail = [];
      const wholesale = [];
      (adhoc.stores || []).forEach((row) => {
        const item = { id: row.storeId, name: row.storeName };
        if (Number(row.scope) === COMMISSION_STORE_SCOPE.RETAIL) {
          retail.push(item);
        } else {
          wholesale.push(item);
        }
      });

      this.model = {
        name: adhoc.name || '',
        description: adhoc.description || '',
        startDate: this.toInputDate(adhoc.startDate),
        endDate: this.toInputDate(adhoc.endDate),
        status: Number(adhoc.status) || COMMISSION_ADHOC_STATUS.ACTIVE,
        applyAllRetail: Number(adhoc.applyAllRetailStores) === 1,
        applyAllWholesale: Number(adhoc.applyAllWholesaleStores) === 1,
        retailStores: retail,
        wholesaleStores: wholesale,
        details: (adhoc.details || []).map((row) => {
          const rates = {};
          (row.employeeRates || []).forEach((rate) => {
            rates[rate.jobTitleId] = rate.amount;
          });
          return {
            type: Number(row.type),
            referenceId: row.referenceId,
            code: row.code || '',
            name: row.name || '',
            amount1: row.amount1,
            amount1Operator: Number(row.amount1Operator) || 1,
            amount2: row.amount2,
            amount2Operator: Number(row.amount2Operator) || 1,
            conditionLogic:
              Number(row.conditionLogic) || COMMISSION_ADHOC_CONDITION_LOGIC.OR,
            rewardMode:
              Number(row.rewardMode) || COMMISSION_ADHOC_REWARD_MODE.MAX,
            conditionCategories: (row.conditions || []).map((c) => ({
              id: c.commissionAdhocCategoryId,
              name: c.name,
            })),
            rates,
          };
        }),
      };
      this.componentKey += 1;
    },

    /* ------------------------------ Ngày tháng ----------------------------- */
    toApiDate(value) {
      if (!value) {
        return null;
      }
      const parsed = moment(value, DATE_INPUT_FORMAT, true);
      return parsed.isValid() ? parsed.format(DATE_API_FORMAT) : null;
    },
    toInputDate(value) {
      return value ? moment(value).format(DATE_INPUT_FORMAT) : '';
    },

    /* ------------------------------ Phạm vi CH ----------------------------- */
    onToggleApplyAllRetail(checked) {
      if (checked) {
        this.model.retailStores = [];
      }
    },
    onToggleApplyAllWholesale(checked) {
      if (checked) {
        this.model.wholesaleStores = [];
      }
    },

    /* ---------------------------- Dòng chi tiết ---------------------------- */
    /** Form rỗng cho modal — dùng cả lúc khởi tạo data lẫn lúc reset */
    emptyDetailForm() {
      return {
        type: COMMISSION_ADHOC_ITEM_TYPE.PRODUCT,
        referenceId: null,
        name: '',
        ...emptyAdhocRule(),
        rates: {},
      };
    },
    openDetailModal() {
      this.resetDetailForm();
      this.detailModalVisible = true;
    },
    resetDetailForm() {
      this.detailForm = this.emptyDetailForm();
      this.itemSuggestions = [];
      this.componentKey += 1;
    },
    /** Chốt dòng: đẩy vào danh sách, dòng đã thêm KHÔNG sửa lại được */
    confirmAddDetail() {
      this.model.details.push({
        ...this.detailForm,
        conditionCategories: [...this.detailForm.conditionCategories],
        rates: { ...this.detailForm.rates },
      });
    },
    /** Xoá dòng phải xác nhận; chương trình đã có thì nhắc ảnh hưởng hoa hồng */
    removeDetail(index) {
      const detail = this.model.details[index];
      if (!detail) {
        return;
      }
      const label = `${this.itemTypeName(detail.type)} "${detail.name}"`;
      const text = this.adhocId
        ? `Xóa dòng ${label} sẽ làm thay đổi hoa hồng AdHoc của nhân viên sau khi lưu chương trình. Bạn có chắc muốn xóa?`
        : `Bạn có chắc muốn xóa dòng ${label} không?`;
      Swal.fire({
        title: 'Xóa dòng chi tiết thưởng',
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        position: 'top',
      }).then((result) => {
        // Tìm lại theo đối tượng: bảng có thể đã thay đổi trong lúc chờ xác nhận
        const current = this.model.details.indexOf(detail);
        if (result.value && current > -1) {
          this.model.details.splice(current, 1);
        }
      });
    },
    showViewDetail(detail) {
      this.viewDetail = detail;
      this.viewDetailVisible = true;
    },
    onDetailTypeChange() {
      // Đổi loại thì item cũ không còn ý nghĩa -> xoá để tránh gửi nhầm id
      this.detailForm.referenceId = null;
      this.detailForm.name = '';
      this.itemSuggestions = [];
      this.componentKey += 1;
    },

    /* --------------------- Hiển thị bảng dòng (chỉ đọc) --------------------- */
    rewardModeName(mode) {
      return COMMISSION_ADHOC_REWARD_MODE_NAME[Number(mode)] || '';
    },
    itemTypeName(type) {
      return COMMISSION_ADHOC_ITEM_TYPE_NAME[Number(type)] || '';
    },
    conditionLogicName(logic) {
      return COMMISSION_ADHOC_CONDITION_LOGIC_NAME[Number(logic)] || '';
    },
    /**
     * Rút gọn số tiền cho dễ đọc: 6.000.000 -> "6 triệu", 500.000 -> "500 nghìn".
     * Số lẻ giữ nguyên dạng đầy đủ để không làm sai lệch con số.
     */
    shortMoney(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return '';
      }
      if (numeric !== 0 && numeric % 1000000 === 0) {
        return `${(numeric / 1000000).toLocaleString('vi-VN')} triệu`;
      }
      if (numeric !== 0 && numeric % 1000 === 0) {
        return `${(numeric / 1000).toLocaleString('vi-VN')} nghìn`;
      }
      return `${numeric.toLocaleString('vi-VN')} đ`;
    },

    /**
     * Đọc điều kiện doanh thu thành câu tiếng Việt.
     *
     * Quy ước của engine (calculator.isRevenueInRange):
     *   vế trái  = amount1 <toán tử> DT   ⇒ "≤" nghĩa là DT TỪ amount1 trở lên
     *   vế phải  = DT <toán tử> amount2
     * Chiều này dễ nhầm nên phải diễn giải lại cho người nhập tự soát.
     *
     * @returns {{text: string, warn: boolean}}
     */
    describeRevenue(detail) {
      const OP = { NONE: 1, LT: 2, GT: 3, LTE: 4, GTE: 5 };
      const amount1 = this.toAmount(detail.amount1);
      const amount2 = this.toAmount(detail.amount2);
      const operator1 = Number(detail.amount1Operator);
      const operator2 = Number(detail.amount2Operator);
      const has1 = amount1 !== null && operator1 !== OP.NONE;
      const has2 = amount2 !== null && operator2 !== OP.NONE;

      if (!has1 && !has2) {
        return { text: 'Áp dụng cho mọi mức doanh thu', warn: false };
      }

      const lowIsFloor = operator1 === OP.LTE || operator1 === OP.LT;
      const highIsCeil = operator2 === OP.LTE || operator2 === OP.LT;

      if (has1 && has2 && lowIsFloor && highIsCeil) {
        // Cận dưới lớn hơn cận trên -> không hoá đơn nào lọt, phải cảnh báo
        if (amount1 > amount2) {
          return {
            warn: true,
            text:
              `Khoảng không hợp lệ: cận dưới (${this.shortMoney(amount1)}) lớn hơn ` +
              `cận trên (${this.shortMoney(amount2)}) — sẽ không có hoá đơn nào đạt`,
          };
        }
        const from =
          operator1 === OP.LTE
            ? `từ ${this.shortMoney(amount1)}`
            : `trên ${this.shortMoney(amount1)}`;
        const to =
          operator2 === OP.LTE
            ? `đến ${this.shortMoney(amount2)}`
            : `dưới ${this.shortMoney(amount2)}`;
        return { text: `Doanh thu ${from} ${to}`, warn: false };
      }

      const parts = [];
      if (has1) {
        if (operator1 === OP.LTE) {
          parts.push(`Doanh thu từ ${this.shortMoney(amount1)} trở lên`);
        } else if (operator1 === OP.LT) {
          parts.push(`Doanh thu lớn hơn ${this.shortMoney(amount1)}`);
        } else if (operator1 === OP.GTE) {
          parts.push(`Doanh thu tối đa ${this.shortMoney(amount1)}`);
        } else if (operator1 === OP.GT) {
          parts.push(`Doanh thu nhỏ hơn ${this.shortMoney(amount1)}`);
        }
      }
      if (has2) {
        if (operator2 === OP.LTE) {
          parts.push(`Doanh thu tối đa ${this.shortMoney(amount2)}`);
        } else if (operator2 === OP.LT) {
          parts.push(`Doanh thu nhỏ hơn ${this.shortMoney(amount2)}`);
        } else if (operator2 === OP.GTE) {
          parts.push(`Doanh thu từ ${this.shortMoney(amount2)} trở lên`);
        } else if (operator2 === OP.GT) {
          parts.push(`Doanh thu lớn hơn ${this.shortMoney(amount2)}`);
        }
      }
      return { text: parts.join(' và '), warn: false };
    },

    /** Bảng dòng đã thêm dùng LẠI đúng câu mô tả của preview (NT-3) */
    describeAmount(detail) {
      return this.describeRevenue(detail).text;
    },
    /** Danh sách mức thưởng > 0, kèm tên vị trí */
    describeRates(detail) {
      return Object.keys(detail.rates)
        .map((jobTitleId) => {
          const amount = this.toAmount(detail.rates[jobTitleId]);
          const job = this.jobTitles.find((row) => Number(row.id) === Number(jobTitleId));
          return {
            jobTitleId,
            name: job ? job.name : `#${jobTitleId}`,
            amount: this.formatMoney(amount),
          };
        })
        .filter((row) => row.amount !== '0');
    },
    formatMoney(value) {
      const numeric = this.toAmount(value);
      return numeric === null ? '' : Number(numeric).toLocaleString('vi-VN');
    },

    /* ------------------- Tìm sản phẩm / danh mục (dùng chung) ---------------- */
    // Dùng LẠI đúng API mà nhóm hoa hồng đang dùng: một endpoint tra được cả
    // sản phẩm lẫn danh mục, phân biệt bằng typeSearch.
    onSearchItem(text) {
      this.detailForm.name = text;
      if (!text) {
        this.detailForm.referenceId = null;
      }
      this.debounceSearchItem(text, this.detailForm.type);
    },
    debounceSearchItem: debounce(function (text, type) {
      if (!text) {
        this.itemSuggestions = [];
        return;
      }
      const typeSearch =
        Number(type) === COMMISSION_ADHOC_ITEM_TYPE.CATEGORY
          ? COMMISSION_SEARCH_TYPE.CATEGORY
          : COMMISSION_SEARCH_TYPE.PRODUCT;
      ApiService.setHeader();
      ApiService.get(
        `productSearch/search-by-product-category?searchProduct=${encodeURIComponent(
          text,
        )}&typeSearch=${typeSearch}&typeOfProduct=0`,
      )
        .then(({ data }) => {
          this.itemSuggestions = (data.data || []).map((item) => ({
            id: item.id,
            name: item.name || item.productName,
            suggestionName: item.code
              ? `${item.code} - ${item.name || item.productName}`
              : item.name || item.productName,
          }));
        })
        .catch(() => {
          this.itemSuggestions = [];
        });
    }, TIME_TRIGGER),
    onSelectItem(option) {
      this.detailForm.referenceId = option.item.id;
      this.detailForm.name = option.item.name;
    },

    /* ------------------------------ Nhập Excel ----------------------------- */
    showImportModal() {
      this.resetImportState();
      this.$refs['import-modal'].show();
    },
    hideImportModal() {
      this.$refs['import-modal'].hide();
    },
    resetImportState() {
      this.file = null;
      this.rawImportRows = [];
      this.importRule = emptyAdhocRule();
      this.clearImportResult();
    },
    /** Bỏ kết quả kiểm tra cũ — file hoặc công thức đổi thì phải kiểm tra lại */
    clearImportResult() {
      this.validatedItems = [];
      this.importErrors = [];
      this.currentErrorPage = 1;
      this.currentPreviewPage = 1;
    },
    /* ------------------- Áp công thức cho tất cả dòng ---------------------- */
    openBulkRuleModal() {
      this.bulkRule = emptyAdhocRule();
      this.bulkRuleVisible = true;
    },
    /** Ghi đè điều kiện + cách tính của MỌI dòng; mức thưởng theo vị trí giữ nguyên */
    applyBulkRule() {
      this.model.details = this.model.details.map((detail) => ({
        ...detail,
        ...this.bulkRule,
        conditionCategories: [...this.bulkRule.conditionCategories],
      }));
      makeToastSuccess(
        `Đã áp công thức cho ${this.model.details.length} dòng. Bấm Lưu để ghi nhận.`,
      );
    },
    /** File mẫu sinh ngay ở client: cột cố định + 1 cột cho mỗi vị trí đang tải */
    downloadTemplate() {
      if (!this.jobTitles.length) {
        makeToastFaile('Chưa tải được danh sách vị trí công việc!');
        return;
      }
      const header = [
        IMPORT_COLUMN.TYPE,
        IMPORT_COLUMN.CODE,
        IMPORT_COLUMN.NAME,
        ...this.jobTitles.map((job) => job.name),
      ];
      const example = [
        'Sản phẩm',
        'SP001',
        'Tên sản phẩm ví dụ',
        ...this.jobTitles.map((job, index) => (index === 0 ? 100000 : 50000)),
      ];
      const worksheet = xlsx.utils.aoa_to_sheet([header, example]);
      worksheet['!cols'] = header.map((title) => ({
        wch: Math.max(12, String(title).length + 2),
      }));
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, 'Chi tiet AdHoc');
      xlsx.writeFile(workbook, 'Mau_import_chi_tiet_AdHoc.xlsx');
    },
    /** Chuẩn hoá ô Excel về chuỗi chữ thường để so khớp không phân biệt hoa/thường */
    normalizeCell(value) {
      if (value === null || value === undefined) {
        return '';
      }
      return String(value).normalize('NFC').trim().toLowerCase();
    },
    /** Ô trống -> `undefined` (API tự lấy mặc định), chữ lạ -> `null` (API báo lỗi) */
    mapImportValue(value, map) {
      const text = this.normalizeCell(value);
      if (!text) {
        return undefined;
      }
      return map[text] !== undefined ? map[text] : null;
    },
    /**
     * Dòng gửi API kiểm tra: chỉ sản phẩm/danh mục + mức thưởng.
     * Điều kiện và cách tính nhập SAU khi file sạch lỗi, gắn vào lúc Import.
     */
    toImportRow(row, index) {
      const type = this.mapImportValue(row[IMPORT_COLUMN.TYPE], IMPORT_ITEM_TYPE_MAP);
      return {
        row: index + 2, // dòng 1 là tiêu đề trong file Excel
        type: type === undefined ? null : type,
        code:
          row[IMPORT_COLUMN.CODE] !== undefined
            ? String(row[IMPORT_COLUMN.CODE]).trim()
            : '',
        name:
          row[IMPORT_COLUMN.NAME] !== undefined
            ? String(row[IMPORT_COLUMN.NAME]).trim()
            : '',
        amount1: null,
        amount1Operator: 1,
        amount2: null,
        amount2Operator: 1,
        conditionCategoryNames: [],
        // Mỗi vị trí một cột, tiêu đề = đúng tên vị trí trong file mẫu
        employeeRates: this.jobTitles
          .map((job) => ({
            jobTitleId: Number(job.id),
            amount: this.toAmount(row[job.name]) || 0,
          }))
          .filter((rate) => rate.amount > 0),
      };
    },
    selectFile(event) {
      this.rawImportRows = [];
      this.clearImportResult();
      const selectedFile = event.target.files[0];
      if (!selectedFile) {
        return;
      }
      if (!/\.(xls|xlsx)$/.test(selectedFile.name.toLowerCase())) {
        this.file = null;
        this.$refs['file-input'].reset();
        makeToastFaile(
          'Định dạng file không hợp lệ. Vui lòng chọn file .xls hoặc .xlsx',
        );
        return;
      }
      if (!this.jobTitles.length) {
        makeToastFaile('Chưa tải được danh sách vị trí công việc!');
        return;
      }
      const fileReader = new FileReader();
      fileReader.onload = (ev) => {
        try {
          const workbook = xlsx.read(ev.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const rows = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], {
            blankRows: false,
          });
          if (!rows.length) {
            makeToastFaile('File không có dòng dữ liệu nào!');
            return;
          }
          if (rows.length > COMMISSION_IMPORT_MAX_ROWS) {
            makeToastFaile(
              `File vượt quá ${COMMISSION_IMPORT_MAX_ROWS} dòng cho phép mỗi lần nhập.`,
            );
            return;
          }
          this.rawImportRows = rows;
          // Chọn file xong kiểm tra luôn — không bắt bấm thêm một nút
          this.validateImport();
        } catch (error) {
          makeToastFaile('Không đọc được nội dung file Excel!');
        }
      };
      fileReader.readAsBinaryString(selectedFile);
    },
    validateImport() {
      if (!this.rawImportRows.length) {
        makeToastFaile('Chưa có dữ liệu để kiểm tra!');
        return;
      }
      this.isValidating = true;
      this.clearImportResult();
      ApiService.setHeader();
      ApiService.post('commission-adhocs/validate-import-details', {
        rows: this.rawImportRows.map((row, index) => this.toImportRow(row, index)),
      })
        .then(({ data }) => {
          const result = data.data || {};
          this.importErrors = result.errors || [];
          this.validatedItems = result.validItems || [];
          if (this.importErrors.length) {
            makeToastFaile(
              `File có ${this.importErrors.length} lỗi, chưa thể lưu.`,
            );
          }
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Kiểm tra dữ liệu thất bại!'),
          );
        })
        .finally(() => {
          this.isValidating = false;
        });
    },
    /** Đổ dòng hợp lệ vào bảng chi tiết, bỏ qua sản phẩm/danh mục đã có */
    applyImport() {
      if (!this.canApplyImport) {
        return;
      }
      if (this.describeRevenue(this.importRule).warn) {
        makeToastFaile('Điều kiện doanh thu không hợp lệ!');
        return;
      }
      const rule = this.importRule;
      const existing = new Set(
        this.model.details.map((detail) => `${detail.type}|${detail.referenceId}`),
      );
      let duplicated = 0;
      this.validatedItems.forEach((item) => {
        const key = `${Number(item.type)}|${item.referenceId}`;
        if (existing.has(key)) {
          duplicated += 1;
          return;
        }
        existing.add(key);
        const rates = {};
        (item.employeeRates || []).forEach((rate) => {
          rates[rate.jobTitleId] = rate.amount;
        });
        this.model.details.push({
          type: Number(item.type),
          referenceId: item.referenceId,
          code: item.code || '',
          name: item.name || '',
          ...rule,
          conditionCategories: [...rule.conditionCategories],
          rates,
        });
      });
      const added = this.validatedItems.length - duplicated;
      makeToastSuccess(
        duplicated
          ? `Đã thêm ${added} dòng, bỏ qua ${duplicated} dòng đã có trong chương trình.`
          : `Đã thêm ${added} dòng từ file Excel.`,
      );
      this.hideImportModal();
    },

    /* ------------------------------- Lưu ----------------------------------- */
    buildPayload() {
      // API nhận danh sách phẳng [{storeId, scope}] — gộp hai kênh lại
      const stores = [];
      if (!this.model.applyAllRetail) {
        this.model.retailStores.forEach((store) => {
          stores.push({ storeId: store.id, scope: COMMISSION_STORE_SCOPE.RETAIL });
        });
      }
      if (!this.model.applyAllWholesale) {
        this.model.wholesaleStores.forEach((store) => {
          stores.push({ storeId: store.id, scope: COMMISSION_STORE_SCOPE.WHOLESALE });
        });
      }

      return {
        name: (this.model.name || '').trim(),
        description: this.model.description,
        startDate: this.toApiDate(this.model.startDate),
        endDate: this.toApiDate(this.model.endDate),
        status: this.model.status,
        applyAllRetailStores: this.model.applyAllRetail ? 1 : 0,
        applyAllWholesaleStores: this.model.applyAllWholesale ? 1 : 0,
        stores,
        details: this.model.details.map((detail, index) => ({
          type: detail.type,
          referenceId: detail.referenceId,
          amount1: this.toAmount(detail.amount1),
          amount1Operator: detail.amount1Operator,
          amount2: this.toAmount(detail.amount2),
          amount2Operator: detail.amount2Operator,
          conditionLogic: detail.conditionLogic,
          rewardMode: detail.rewardMode,
          conditionCategoryIds: (detail.conditionCategories || []).map((c) => c.id),
          sortOrder: index,
          employeeRates: Object.keys(detail.rates)
            .map((jobTitleId) => ({
              jobTitleId: Number(jobTitleId),
              amount: this.toAmount(detail.rates[jobTitleId]) || 0,
            }))
            .filter((rate) => rate.amount > 0),
        })),
      };
    },
    /** Ô nhập tiền cho phép gõ dấu phân cách — bỏ hết ký tự không phải số */
    toAmount(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const numeric = Number(String(value).replace(/[^0-9.-]/g, ''));
      return Number.isNaN(numeric) ? null : numeric;
    },
    save() {
      const payload = this.buildPayload();

      if (!payload.name) {
        return makeToastFaile('Vui lòng nhập tên chương trình');
      }
      if (!payload.startDate) {
        return makeToastFaile('Vui lòng chọn ngày bắt đầu');
      }
      if (!payload.endDate) {
        return makeToastFaile('Vui lòng chọn ngày kết thúc');
      }
      if (
        !payload.applyAllRetailStores &&
        !payload.applyAllWholesaleStores &&
        payload.stores.length === 0
      ) {
        return makeToastFaile('Vui lòng chọn cửa hàng áp dụng');
      }
      if (payload.details.length === 0) {
        return makeToastFaile('Vui lòng thêm ít nhất một dòng chi tiết');
      }
      const missing = payload.details.findIndex((row) => !row.referenceId);
      if (missing > -1) {
        return makeToastFaile(`Dòng ${missing + 1}: chưa chọn sản phẩm/danh mục`);
      }
      const noRate = payload.details.findIndex((row) => row.employeeRates.length === 0);
      if (noRate > -1) {
        return makeToastFaile(
          `Dòng ${noRate + 1}: chưa nhập mức thưởng cho vị trí nào`,
        );
      }

      this.isSaving = true;
      ApiService.setHeader();
      const request = this.adhocId
        ? ApiService.put(`commission-adhocs/${this.adhocId}`, payload)
        : ApiService.post('commission-adhocs', payload);

      request
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.goBack();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không lưu được chương trình'),
          );
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    goBack() {
      this.$router.push({ name: 'commission-adhocs' });
    },
  },
};
</script>

<style scoped>
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #181c32;
  padding-bottom: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebedf3;
}
.detail-card {
  border: 1px solid #ebedf3;
  border-radius: 6px;
  overflow: hidden;
}
.detail-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f3f6f9;
  padding: 8px 16px;
  font-weight: 600;
  font-size: 13px;
  color: #181c32;
}
.detail-card__body {
  padding: 16px;
}
/* ------------------------------ Modal thêm dòng ----------------------------- */
/* Mỗi bước một khối có gạch phân cách, trừ khối cuối */
.form-block {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #ebedf3;
}
.form-block--last {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: none;
}
.form-block__title {
  font-size: 13px;
  font-weight: 600;
  color: #181c32;
  margin-bottom: 12px;
}
/* Dòng chi tiết bấm được để xem */
.table >>> .detail-row {
  cursor: pointer;
}
.import-guide {
  font-size: 13px;
  color: #3f4254;
  padding-left: 20px;
}
.form-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}

/* Dòng đọc lại điều kiện — xanh khi hợp lệ, vàng khi cấu hình vô nghĩa */
.range-preview {
  font-size: 13px;
  font-weight: 500;
  color: #187de4;
  background: #f1faff;
  border: 1px solid #cfe7fb;
  border-radius: 4px;
  padding: 8px 12px;
}
.range-preview--warn {
  color: #b8860b;
  background: #fff8e6;
  border-color: #ffe2a8;
}

/* Lưới mức thưởng: các ô đều nhau, tự xuống dòng theo bề rộng modal */
.rate-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
}
.rate-cell__label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
